import { IButtonContainer, IButtonDefault, IButtonSmall, IArrowButton } from './buttonSlide.models';
import styled from '@emotion/styled';
import { media, theme } from '@vfit/shared/themes';

export const ButtonContainer = styled.div<IButtonContainer>`
  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-20px);
    }
    60% {
      transform: translateY(-10px);
    }
  }

  span {
    font-family: ${theme.typography.fontFamily.secondary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.body3};
    line-height: ${theme.typography.lineHeight.body3};
    text-align: center;
    letter-spacing: ${theme.letterSpacing.wide};
    text-transform: uppercase;
    color: ${theme.colors.text.primary};
  }

  animation: ${({ animated }) => (animated ? 'bounce 2s infinite' : 'none')};
`;

export const ButtonDefault = styled.button<IButtonDefault>`
  width: 100%;
  padding: 10.5px 15px;
  margin: 0;

  background: ${({ buttonColor, disabled, disabledButtonColor }) =>
    disabled
      ? disabledButtonColor || theme.colors.brand.primaryDisabled
      : buttonColor || theme.colors.text.inverse};

  border-radius: ${({ isApp, isModalFullContainer }) =>
    isApp || isModalFullContainer ? theme.spacing.sm : theme.spacing.xl};

  border: 1px solid ${({ borderColor }) => borderColor || theme.colors.text.inverse};

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;

  span {
    color: ${({ labelColor }) => labelColor || theme.colors.text.primary};
  }

  /* HOVER (solo desktop) */
  ${media.desktop`
    &:hover {
      background: ${({ hoverColor, buttonColor, disabled }) =>
        disabled
          ? theme.colors.text.disabled
          : hoverColor || buttonColor || theme.colors.text.inverse};

      border: 1px solid ${({ hoverColor, borderColor }) =>
        hoverColor ? 'transparent' : borderColor || theme.colors.text.inverse};

      span {
        color: ${({ hoverTextColor, labelColor }) =>
          hoverTextColor || labelColor || theme.colors.text.primary};
      }
    }
  `}

  /* ACTIVE */
  &:active {
    background: ${({ clickColor, buttonColor }) =>
      clickColor || buttonColor || theme.colors.text.inverse};

    border: 1px solid
      ${({ clickColor, borderColor }) =>
        clickColor ? 'transparent' : borderColor || theme.colors.text.inverse};

    span {
      color: ${({ clickTextColor, labelColor }) =>
        clickTextColor || labelColor || theme.colors.text.primary};
    }
  }

  /* DISABLED */
  &:disabled {
    background-color: ${({ disabledButtonColor }) =>
      disabledButtonColor || theme.colors.text.inverse};

    border: 1px solid
      ${({ disabledBorderColor }) => disabledBorderColor || theme.colors.text.disabled};

    span {
      color: ${({ disabledTextColor }) => disabledTextColor || theme.colors.text.disabled};
    }
  }

  /* MOBILE FIX */
  @media (hover: none) and (pointer: coarse) {
    &:hover {
      background: ${({ buttonColor }) => buttonColor || theme.colors.text.inverse};

      border: 1px solid ${({ borderColor }) => borderColor || theme.colors.text.inverse};

      span {
        color: ${({ labelColor }) => labelColor || theme.colors.text.primary};
      }
    }
  }

  ${media.tablet`
    padding: 10px 24px;
  `}

  ${media.desktop`
    padding: 10px 24px;
  `}
`;

export const ButtonSmall = styled.button<IButtonSmall>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${({ size }) => (size ? `${size}px` : '60px')};
  height: ${({ size }) => (size ? `${size}px` : '60px')};

  border-radius: ${({ isApp }) => (isApp ? theme.spacing.sm : theme.spacing['2xl'])};

  border: ${({ borderColor }) => (borderColor ? `1px solid ${borderColor}` : 'none')};

  cursor: pointer;

  background: ${({ buttonColor }) => buttonColor || theme.colors.text.inverse};

  transition: all 0.3s ease;

  &:hover {
    background: ${({ hoverColor, buttonColor }) =>
      hoverColor || buttonColor || theme.colors.text.inverse};
  }

  &:active {
    background: ${({ clickColor, buttonColor }) =>
      clickColor || buttonColor || theme.colors.text.inverse};
  }
`;

export const ArrowButton = styled.div<IArrowButton>`
  display: flex;
  align-items: center;
  justify-content: center;

  transform: rotate(${({ rotation }) => `${rotation}deg`});

  svg {
    width: ${({ arrowWidth }) => (arrowWidth ? `${arrowWidth}px` : '9px')};
    height: ${({ arrowHeigth }) => (arrowHeigth ? `${arrowHeigth}px` : '16px')};

    path {
      stroke: ${({ color }) => color};
    }
  }
`;

